// src/portfolio/CommandPalette.jsx — ⌘K palette with fuzzy match
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FILE_INDEX, COMMANDS } from './data';
import { FileGlyph, MONO } from './icons';

function fuzzyScore(text, needle) {
  let ti = 0, ni = 0, score = 0, run = 0;
  while (ti < text.length && ni < needle.length) {
    if (text[ti] === needle[ni]) { score += 2 + run; run += 1; ni++; }
    else { run = 0; }
    ti++;
  }
  return ni === needle.length ? score : -Infinity;
}

function highlightMatch(text, needle, theme) {
  if (!needle.trim()) return text;
  const lower = text.toLowerCase();
  const n = needle.toLowerCase();
  const out = []; let ti = 0, ni = 0, key = 0;
  while (ti < text.length) {
    if (ni < n.length && lower[ti] === n[ni]) {
      out.push(<span key={key++} style={{ color: theme.accent, fontWeight: 600 }}>{text[ti]}</span>);
      ni++;
    } else {
      out.push(<React.Fragment key={key++}>{text[ti]}</React.Fragment>);
    }
    ti++;
  }
  return out;
}

export default function CommandPalette({ theme, open, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const items = useMemo(() => {
    const fileItems = FILE_INDEX.map((f) => ({
      id: 'file.' + f.id, kind: 'file', fileId: f.id, label: f.path, hint: f.kind, fileKind: f.kind,
    }));
    const cmdItems = COMMANDS.map((c) => ({ ...c, kind: 'cmd', hint: c.kbd, label: c.label }));
    return [...fileItems, ...cmdItems];
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items
      .map((it) => ({ it, score: fuzzyScore(it.label.toLowerCase(), needle) }))
      .filter((x) => x.score > -Infinity)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.it);
  }, [q, items]);

  useEffect(() => {
    if (open) {
      setQ('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => { setCursor(0); }, [q]);

  if (!open) return null;

  const pick = (it) => {
    if (!it) return;
    if (it.kind === 'file') onPick({ kind: 'file', fileId: it.fileId });
    else onPick({ kind: 'cmd', id: it.id });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
      zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '8vh',
    }} onClick={onClose}>
      <div
        className="palette-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)',
          background: theme.surface, border: `1px solid ${theme.borderHi}`, borderRadius: 8,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: `1px solid ${theme.border}` }}>
          <span style={{ color: theme.dim, fontFamily: MONO, fontSize: 13 }}>›</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { onClose(); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
              else if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
              else if (e.key === 'Enter')     { e.preventDefault(); pick(filtered[cursor]); }
            }}
            placeholder="Type a file name, or a command…"
            style={{
              flex: 1, background: 'transparent', border: 0, outline: 'none',
              fontFamily: "'Inter'", fontSize: 15, color: theme.fgStrong, padding: 0,
            }}
          />
          <span style={{ fontFamily: MONO, fontSize: 10, color: theme.dim }}>esc</span>
        </div>
        <div style={{ maxHeight: 380, overflow: 'auto', padding: '4px 0' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: theme.dim, fontSize: 13 }}>
              No matches. Try "cibc", "email", or "resume".
            </div>
          )}
          {filtered.slice(0, 60).map((it, i) => (
            <button
              key={it.id}
              type="button"
              onMouseEnter={() => setCursor(i)}
              onClick={() => pick(it)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 14px', textAlign: 'left',
                background: cursor === i ? theme.surface2 : 'transparent',
                color: theme.fg, fontFamily: it.kind === 'file' ? MONO : "'Inter'", fontSize: 13,
                borderLeft: cursor === i ? `2px solid ${theme.accent}` : '2px solid transparent',
                border: 0, cursor: 'pointer',
              }}
            >
              {it.kind === 'file'
                ? <FileGlyph kind={it.fileKind} theme={theme} size={12} />
                : <span style={{ width: 16, color: theme.sx.link, fontFamily: MONO, fontSize: 13, textAlign: 'center' }}>{it.icon}</span>
              }
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {highlightMatch(it.label, q, theme)}
              </span>
              <span style={{ fontSize: 10, color: theme.dim, fontFamily: MONO, letterSpacing: '.04em' }}>{it.hint}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 14, padding: '8px 14px', borderTop: `1px solid ${theme.border}`, fontFamily: MONO, fontSize: 10, color: theme.dim }}>
          <span>↑↓ navigate</span><span>⏎ select</span><span>esc dismiss</span>
        </div>
      </div>
    </div>
  );
}
